import { gql } from '@apollo/client/core'
import Role from 'src/models/access-control/Role'
import Activity from 'src/models/activity/Activity'
import ActivitySpace from 'src/models/activity-space/ActivitySpace'
import BookingFeeType from 'src/models/booking-fee-type/BookingFeeType'
import Circuit from 'src/models/circuit/Circuit'
import DeliveryLocation from 'src/models/delivery-location/DeliveryLocation'
import DeliveryType from 'src/models/delivery-type/DeliveryType'
import Discount from 'src/models/discount/Discount'
import EmployeeJobType from 'src/models/employee-job-type/EmployeeJobType'
import GlobalWidget from 'src/models/global-widget/GlobalWidget'
import InventoryItem from 'src/models/inventory-item/InventoryItem'
import Category from 'src/models/item/Category'
import Item from 'src/models/item/Item'
import ItemClass from 'src/models/item/ItemClass'
import ItemVariation from 'src/models/item/ItemVariation'
import KitchenDisplay from 'src/models/kitchen-display/KitchenDisplay'
import KitchenDisplayType from 'src/models/kitchen-display/KitchenDisplayType'
import Location from 'src/models/location/Location'
import LocationGroup from 'src/models/location/LocationGroup'
import MembershipType from 'src/models/membership-type/MembershipType'
import Model, { configurationStatus, executeMutation, GqlError, Integer, JsonObject } from 'src/models/model'
import Modifier from 'src/models/modifier-set/Modifier'
import ModifierSet from 'src/models/modifier-set/ModifierSet'
import TitleClass from 'src/models/movie/TitleClass'
import NewsItem from 'src/models/news-item/NewsItem'
import NewsItemClass from 'src/models/news-item/NewsItemClass'
import Tax from 'src/models/order/Tax'
import ItemPackage from 'src/models/package/ItemPackage'
import Navigation from 'src/models/page/Navigation'
import Page from 'src/models/page/Page'
import PagerRequestType from 'src/models/pager-request/PagerRequestType'
import PaymentType from 'src/models/payment/PaymentType'
import PriceTier from 'src/models/price-tier/PriceTier'
import PrintTemplate from 'src/models/print-template/PrintTemplate'
import RegisterGrid from 'src/models/register/RegisterGrid'
import RegisterStation from 'src/models/register/RegisterStation'
import RegisterType from 'src/models/register/RegisterType'
import ReportComponent from 'src/models/report/ReportComponent'
import SiteSchedule from 'src/models/schedule/SiteSchedule'
import TimeSlot from 'src/models/schedule/TimeSlot'
import Screen from 'src/models/screen/Screen'
import SeatChartAreaCategory from 'src/models/screen/SeatChartAreaCategory'
import PriceCard from 'src/models/showing/PriceCard'
import ShowingBadge from 'src/models/showing/ShowingBadge'
import TicketType from 'src/models/showing/TicketType'
import SignageMediaClass from 'src/models/signage/SignageMediaClass'
import Site from 'src/models/site/Site'
import SiteLocationZone from 'src/models/site-location-zone/SiteLocationZone'
import MarketingSubscription from 'src/models/subscription/MarketingSubscription'
import UserClass from 'src/models/user-class/UserClass'
import VoucherPackage from 'src/models/voucher/VoucherPackage'
import VoucherType from 'src/models/voucher/VoucherType'

export default class ClientConfig extends Model {
  static name = 'ClientConfig'

  static noId = true
  static isConfiguration = true

  static define () {
    this.fields = {
      currentSite: {
        type: Site,
        output: true
      },
      currentCircuit: {
        type: Circuit,
        output: true
      },

      defaultMembershipType: {
        type: MembershipType,
        reference: true,
        output: true
      },

      sites: {
        type: [Site],
        output: true
      },

      bookingFeeTypes: {
        type: [BookingFeeType],
        output: true
      },
      userClasses: {
        type: [UserClass],
        output: true
      },
      voucherTypes: {
        type: [VoucherType],
        output: true
      },
      voucherPackages: {
        type: [VoucherPackage],
        output: true
      },
      discounts: {
        type: [Discount],
        output: true
      },
      membershipTypes: {
        type: [MembershipType],
        output: true
      },
      priceTiers: {
        type: [PriceTier],
        output: true
      },
      items: {
        type: [Item],
        output: true
      },
      itemVariations: {
        type: [ItemVariation],
        output: true
      },
      inventoryItems: {
        type: [InventoryItem],
        output: true
      },
      modifiers: {
        type: [Modifier],
        output: true
      },
      modifierSets: {
        type: [ModifierSet],
        output: true
      },
      taxes: {
        type: [Tax],
        output: true
      },
      locationGroups: {
        type: [LocationGroup],
        output: true
      },
      locations: {
        type: [Location],
        output: true
      },
      deliveryTypes: {
        type: [DeliveryType],
        output: true
      },
      deliveryLocations: {
        type: [DeliveryLocation],
        output: true
      },
      kitchenDisplayTypes: {
        type: [KitchenDisplayType],
        output: true
      },
      kitchenDisplays: {
        type: [KitchenDisplay],
        output: true
      },
      employeeJobTypes: {
        type: [EmployeeJobType],
        output: true
      },
      itemClasses: {
        type: [ItemClass],
        output: true
      },
      titleClasses: {
        type: [TitleClass],
        output: true
      },
      signageMediaClasses: {
        type: [SignageMediaClass],
        output: true
      },
      categories: {
        type: [Category],
        output: true
      },

      registerStations: {
        type: [RegisterStation],
        output: true
      },
      registerGrids: {
        type: [RegisterGrid],
        output: true
      },
      registerTypes: {
        type: [RegisterType],
        output: true
      },

      roles: {
        type: [Role],
        output: true
      },

      siteLocationZones: {
        type: [SiteLocationZone],
        output: true
      },

      screens: {
        type: [Screen],
        output: true
      },
      siteSchedules: {
        type: [SiteSchedule],
        output: true
      },
      timeSlots: {
        type: [TimeSlot],
        output: true
      },
      activities: {
        type: [Activity],
        output: true
      },
      activitySpaces: {
        type: [ActivitySpace],
        output: true
      },
      priceCards: {
        type: [PriceCard],
        output: true
      },
      itemPackages: {
        type: [ItemPackage],
        output: true
      },
      showingBadges: {
        type: [ShowingBadge],
        output: true
      },
      ticketTypes: {
        type: [TicketType],
        output: true
      },
      seatChartAreaCategories: {
        type: [SeatChartAreaCategory],
        output: true
      },
      publishedPrintTemplates: {
        type: [PrintTemplate],
        output: true
      },
      newsItemClasses: {
        type: [NewsItemClass],
        output: true
      },
      publishedNewsItems: {
        type: [NewsItem],
        output: true
      },
      publishedPages: {
        type: [Page],
        output: true
      },
      publishedGlobalWidgets: {
        type: [GlobalWidget],
        output: true
      },
      navigations: {
        type: [Navigation],
        output: true
      },
      activeMarketingSubscriptions: {
        type: [MarketingSubscription],
        output: true
      },
      marketingSubscriptions: {
        type: [MarketingSubscription],
        output: true
      },

      reportComponents: {
        type: [ReportComponent],
        output: true
      },
      regionReportComponents: {
        type: [ReportComponent],
        output: true
      },
      paymentTypes: {
        type: [PaymentType],
        output: true
      },
      circuitPropertyVersions: {
        type: JsonObject,
        output: true
      },
      sitePropertyVersions: {
        type: JsonObject,
        output: true
      },
      pagerRequestTypes: {
        type: [PagerRequestType],
        output: true
      },

      action: {
        type: String,
        input: true
      },

      version: {
        type: Integer
      }
    }

    this.watchers = [
      [
        function () { return this.version },
        function () {
          if (this.version) {
            configurationStatus.localClientConfigVersion = this.version
          }
        }
      ]
    ]

    this.queries = {
      clientConfigVersion: {
        results: {
          version: Integer
        }
      }
    }

    this.mutations = {
      bulkSetOrder: {
        arguments: {
          typeName: {
            type: String,
            required: true
          },
          orderFieldName: {
            type: String,
            required: true
          },
          orderValues: {
            type: JsonObject,
            required: true
          }
        },
        results: {
          success: Boolean,
          errors: [GqlError]
        }
      }
    }
  }

  unsubscribeFromRemoteModelChanges () {
    if (!_.isNil(this.observationResponse)) {
      this.observationResponse.unsubscribe()
    }
  }

  async subscribeToRemoteModelChanges () {
    this.unsubscribeFromRemoteModelChanges()

    const subscriptionQuery = gql`subscription {
      clientConfigUpdated {
        version
      }
    }`

    const subscriptionName = 'clientConfigUpdated'

    const observer = Model.apollo.subscribe({
      query: subscriptionQuery
    })

    this.observationResponse = observer.subscribe({
      next: (data) => {
        data = data?.data && data?.data[subscriptionName]

        if (_.isNil(data?.version)) return

        if (data.version && data.version === this.configuration.version) {
          console.log('[CACHE] Client Config Versions already match, skipping validation: ' + this.configuration.version)
          configurationStatus.notifiedClientConfigVersion = data.version
          return
        } else if (data.version && data.version < this.configuration.version) {
          console.log('[CACHE] Current Client Version newer than change notification, skipping validation, Current: ' + this.configuration.version + ' Notified: ' + data.version)
          return
        } else {
          console.log('[CACHE] Got notification of a new Client Config Version, getting latest version, Current: ' + this.configuration.version + ' New: ' + data.version)
          configurationStatus.notifiedClientConfigVersion = data.version
        }

        this.validate()
      },
      error (error) {
        console.error(error)
      }
    })
  }

  validateOnAppLaunch () {
    let lastFired = new Date().getTime()
    setTimeout(async () => {
      const now = new Date().getTime()
      // If it's been more than 10 minutes since the app slept
      if (now - lastFired > 1000 * 60 * 10) {
        await this.validate()
      }

      // Check again
      lastFired = now
      this.validateOnAppLaunch()
    }, 500)
  }

  validatePeriodically (minutes = 120) {
    setInterval(
      () => this.validate(),
      1000 * 60 * minutes
    )
  }

  async validate () {
    this.action = 'validate'

    try {
      const { query, response } = await this.save()
      this.resetField('action')
      return { query, response }
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async recompute () {
    this.action = 'recompute'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async validateAndNotify () {
    this.action = 'validate_and_notify'

    try {
      const pushingChangeCount = configurationStatus.changeCount
      configurationStatus.isPushingConfigurationData = true
      await this.save()
      configurationStatus.pushedChangeCount = pushingChangeCount
    } catch (error) {
      console.log(error)
    } finally {
      configurationStatus.isPushingConfigurationData = false
      this.resetField('action')
    }
  }

  async invalidate () {
    this.action = 'invalidate'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  // Wrapping multiple saves with this function will
  // prevent recomputing client cache upon each save
  // supports synchronous calling
  //
  // The recompute will always be async
  async batchSave (performSaves) {
    console.log('[CLIENT-CACHE] Staring Batch Save')
    configurationStatus.batchSaving = true

    // Catch errors to ensure we disable batch saving flag
    try {
      await performSaves()
    } catch (e) {
      console.log('An error occurred during batch save')
      console.log(e)
    }

    configurationStatus.batchSaving = false

    console.log('[CLIENT-CACHE] Finished Batch Save, Validating Config')

    if (configurationStatus.automatic) {
      this.configuration.validateAndNotify()
    } else {
      this.configuration.validate()
    }
  }

  async bulkSetOrder (models, orderFieldName) {
    if (models.length === 0) return

    const typeName = models[0].typeName

    const orderValues = _.reduce(models, (acc, model) => {
      const order = model[orderFieldName]
      acc[model.id] = order
      return acc
    }, {})

    const params = {
      typeName,
      orderFieldName,
      orderValues
    }

    const resultStructure = {
      success: true,
      errors: {
        structure: {
          code: true,
          description: true
        }
      }
    }

    const results = await executeMutation(
      'bulkSetOrder',
      params,
      resultStructure
    )
    return results
  }
}
